import { AlertTriangle, CheckCircle2, X } from 'lucide-react';
import {
  Component,
  createContext,
  type ErrorInfo,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useStore } from 'zustand';
import { secCapReasonApi } from './api';
import { ReasonAdmin } from './components/ReasonAdmin';
import { ReasonEditor } from './components/ReasonEditor';
import { ReasonHeader } from './components/ReasonHeader';
import { ReasonLeaderboard } from './components/ReasonLeaderboard';
import { ReasonMyTasks } from './components/ReasonMyTasks';
import { ReasonResults } from './components/ReasonResults';
import { ReasonResultViewer } from './components/ReasonResultViewer';
import { createSecCapReasonStore, type SecCapReasonStoreState } from './store';
import type { ReasonClientEvent, SecCapReasonApiClient } from './types';

type ReasonStore = ReturnType<typeof createSecCapReasonStore>;

const SecCapReasonStoreContext = createContext<ReasonStore | null>(null);
const REASON_EDITOR_SESSION_KEY = 'seccapreason.editor-session.v1';

function parseReasonResultsHash(
  hash: string
): { view: 'results' } | { view: 'result'; episodeId: string; revision?: number } | null {
  const detail = /^#results\/([^/]+)(?:\/revision\/(\d+))?$/.exec(hash);
  if (detail?.[1]) {
    const revision = detail[2] === undefined ? undefined : Number(detail[2]);
    return {
      view: 'result',
      episodeId: decodeURIComponent(detail[1]),
      ...(revision === undefined ? {} : { revision }),
    };
  }
  return hash === '#results' ? { view: 'results' } : null;
}

export interface SecCapReasonAppProps {
  operator: string;
  isAdmin?: boolean;
  client?: SecCapReasonApiClient;
  now?: () => number;
  onSignOut?: () => void;
}

function SecCapReasonStoreProvider({
  client,
  now,
  children,
}: {
  client: SecCapReasonApiClient;
  now?: () => number;
  children: ReactNode;
}) {
  const [store] = useState(() => createSecCapReasonStore(client, now ? { now } : {}));
  return (
    <SecCapReasonStoreContext.Provider value={store}>{children}</SecCapReasonStoreContext.Provider>
  );
}

export function useSecCapReasonStore<T>(selector: (state: SecCapReasonStoreState) => T): T {
  const store = useContext(SecCapReasonStoreContext);
  if (!store) throw new Error('useSecCapReasonStore must be used inside SecCapReasonApp.');
  return useStore(store, selector);
}

function ErrorBanner() {
  const error = useSecCapReasonStore((state) => state.error);
  const clearError = useSecCapReasonStore((state) => state.clearError);
  if (!error) return null;
  return (
    <div className="mx-auto mt-4 flex max-w-[1400px] items-center gap-3 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span className="min-w-0 flex-1">{error}</span>
      <button type="button" onClick={clearError} aria-label="Dismiss error">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

function NoticeBanner() {
  const notice = useSecCapReasonStore((state) => state.notice);
  const noticeAction = useSecCapReasonStore((state) => state.noticeAction);
  const receipt = useSecCapReasonStore((state) => state.lastCommitReceipt);
  const claimNext = useSecCapReasonStore((state) => state.claimNext);
  const claiming = useSecCapReasonStore((state) => state.claiming);
  const clearNotice = useSecCapReasonStore((state) => state.clearNotice);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (!notice || paused) return;
    const timer = window.setTimeout(clearNotice, noticeAction === 'claim-next' ? 10_000 : 5_000);
    return () => window.clearTimeout(timer);
  }, [clearNotice, notice, noticeAction, paused]);
  if (!notice) return null;
  return (
    <div
      className="mx-auto mt-4 flex max-w-[1400px] items-center gap-3 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300"
      role="status"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <CheckCircle2 className="h-4 w-4 shrink-0" />
      <span className="min-w-0 flex-1">
        <span className="block">{notice}</span>
        {receipt ? (
          <span className="mt-1 block font-mono text-[11px] text-emerald-200/80">
            {receipt.environment.toUpperCase()} · {receipt.episode_id} · revision {receipt.revision}{' '}
            · {receipt.ddb_sync_status}
          </span>
        ) : null}
      </span>
      {noticeAction === 'claim-next' ? (
        <button
          type="button"
          disabled={claiming}
          onClick={() => void claimNext()}
          className="h-8 rounded-md border border-emerald-400/40 px-3 text-xs font-medium text-emerald-200 disabled:opacity-50"
        >
          {claiming ? 'Checking...' : 'Check again'}
        </button>
      ) : null}
      <button type="button" onClick={clearNotice} aria-label="Dismiss notice">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

class ReasonEditorBoundary extends Component<
  { children: ReactNode; onRecover: () => void; onRuntimeError: () => void },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('SecCapReason editor runtime error', {
      name: error.name,
      message: error.message,
      componentStack: info.componentStack,
    });
    this.props.onRuntimeError();
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <main className="mx-auto max-w-2xl px-4 py-12 text-brand-text-primary">
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-5">
          <h1 className="text-base font-semibold">The task view stopped unexpectedly.</h1>
          <p className="mt-2 text-sm text-brand-text-secondary">
            Your saved task is still assigned to you. Reload it to continue.
          </p>
          <button
            type="button"
            onClick={() => {
              this.setState({ failed: false });
              this.props.onRecover();
            }}
            className="mt-4 rounded-md bg-brand-orange px-4 py-2 text-sm font-semibold text-white"
          >
            Reload current task
          </button>
        </div>
      </main>
    );
  }
}

function SecCapReasonWorkspace({
  operator,
  isAdmin,
  now,
  onSignOut,
}: Omit<SecCapReasonAppProps, 'client'>) {
  const state = useSecCapReasonStore((current) => current);
  const [, refreshDeadlineState] = useState(0);
  const restoreStarted = useRef(false);
  const applyingResultsHash = useRef(false);
  const currentViewRef = useRef(state.view);
  currentViewRef.current = state.view;
  const nowMs = now?.() ?? Date.now();
  const diagnosticContext = () => ({
    ...(state.activeTask
      ? {
          episode_id: state.activeTask.episode_id,
          revision: state.activeTask.revision,
          segment_index: state.activeSegmentIndex,
        }
      : {}),
    pending_save_count: state.pendingSegmentSaveCount,
    visibility_state: document.visibilityState,
  });

  useEffect(() => {
    const handleClientEvent = (rawEvent: Event) => {
      const detail = (rawEvent as CustomEvent<Record<string, unknown>>).detail;
      if (!detail || typeof detail.event !== 'string') return;
      void state.recordClientEvent({
        event: detail.event as ReasonClientEvent,
        ...diagnosticContext(),
        ...(typeof detail.http_status === 'number' ? { http_status: detail.http_status } : {}),
        ...(typeof detail.error_code === 'string' ? { error_code: detail.error_code } : {}),
      });
    };
    window.addEventListener('seccapreason:client-event', handleClientEvent);
    return () => window.removeEventListener('seccapreason:client-event', handleClientEvent);
  });

  useEffect(() => {
    if (restoreStarted.current) return;
    restoreStarted.current = true;
    void Promise.all([
      state.loadMyTasks(),
      state.loadReasonOptions(),
      state.loadRuntimeInfo(),
    ]).then(async () => {
      try {
        const raw = sessionStorage.getItem(REASON_EDITOR_SESSION_KEY);
        if (!raw) return;
        const saved = JSON.parse(raw) as { episode_id?: unknown; segment_index?: unknown };
        if (typeof saved.episode_id !== 'string' || !Number.isSafeInteger(saved.segment_index)) {
          sessionStorage.removeItem(REASON_EDITOR_SESSION_KEY);
          return;
        }
        void state.recordClientEvent({
          event: 'task_restore_started',
          episode_id: saved.episode_id,
          segment_index: Number(saved.segment_index),
          pending_save_count: 0,
          visibility_state: document.visibilityState,
        });
        const restored = await state.openTask(saved.episode_id, Number(saved.segment_index));
        void state.recordClientEvent({
          event: restored ? 'task_restore_succeeded' : 'task_restore_failed',
          episode_id: saved.episode_id,
          segment_index: Number(saved.segment_index),
          pending_save_count: 0,
          visibility_state: document.visibilityState,
        });
        if (!restored) sessionStorage.removeItem(REASON_EDITOR_SESSION_KEY);
      } catch {
        void state.recordClientEvent({
          event: 'task_restore_failed',
          pending_save_count: 0,
          visibility_state: document.visibilityState,
        });
        sessionStorage.removeItem(REASON_EDITOR_SESSION_KEY);
      }
    });
  }, [
    state.loadMyTasks,
    state.loadReasonOptions,
    state.loadRuntimeInfo,
    state.openTask,
    state.recordClientEvent,
  ]);

  useEffect(() => {
    if (!isAdmin) return;
    const applyHash = async () => {
      const target = parseReasonResultsHash(window.location.hash);
      applyingResultsHash.current = true;
      try {
        if (target?.view === 'result') {
          await state.openResult(target.episodeId, target.revision);
        } else if (target?.view === 'results') {
          await state.setView('results');
        } else if (currentViewRef.current === 'results' || currentViewRef.current === 'result') {
          await state.setView('tasks');
        }
      } finally {
        applyingResultsHash.current = false;
      }
    };
    void applyHash();
    window.addEventListener('hashchange', applyHash);
    window.addEventListener('popstate', applyHash);
    return () => {
      window.removeEventListener('hashchange', applyHash);
      window.removeEventListener('popstate', applyHash);
    };
  }, [isAdmin, state.openResult, state.setView]);

  useEffect(() => {
    if (!isAdmin || applyingResultsHash.current) return;
    const targetHash =
      state.view === 'result' && state.activeResult
        ? `#results/${encodeURIComponent(state.activeResult.episode_id)}/revision/${state.activeResult.revision}`
        : state.view === 'results'
          ? '#results'
          : null;
    if (targetHash && window.location.hash !== targetHash) {
      window.history.pushState(null, '', targetHash);
    }
  }, [isAdmin, state.activeResult, state.view]);

  useEffect(() => {
    if (state.view !== 'editor' || !state.activeTask) return;
    sessionStorage.setItem(
      REASON_EDITOR_SESSION_KEY,
      JSON.stringify({
        episode_id: state.activeTask.episode_id,
        segment_index: state.activeSegmentIndex,
      })
    );
  }, [state.activeSegmentIndex, state.activeTask, state.view]);

  useEffect(() => {
    if (state.view === 'admin' && isAdmin) {
      void Promise.all([state.loadAdminTasks(), state.loadStats()]);
    }
    if (state.view === 'leaderboard' && isAdmin) void state.loadLeaderboard();
    if (state.view === 'results' && isAdmin) void state.loadResults();
  }, [
    isAdmin,
    state.loadAdminTasks,
    state.loadLeaderboard,
    state.loadResults,
    state.loadStats,
    state.view,
  ]);

  useEffect(() => {
    if (state.view !== 'leaderboard' || !isAdmin) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void state.loadLeaderboard();
    }, 60_000);
    return () => window.clearInterval(timer);
  }, [isAdmin, state.loadLeaderboard, state.view]);

  useEffect(() => {
    const currentTime = now?.() ?? Date.now();
    const deadlines = [
      ...state.tasks.map((task) => task.edit_until_ms),
      state.activeTask?.edit_until_ms ?? null,
      ...state.adminTasks.map((task) => task.edit_until_ms),
    ].filter((value): value is number => value != null && value > currentTime);
    if (deadlines.length === 0) return;
    const nextDeadline = Math.min(...deadlines);
    const delayMs = Math.min(60_000, Math.max(25, nextDeadline - currentTime + 25));
    const timer = window.setTimeout(() => refreshDeadlineState((value) => value + 1), delayMs);
    return () => window.clearTimeout(timer);
  }, [now, state.activeTask?.edit_until_ms, state.adminTasks, state.tasks]);

  const handleSignOut = onSignOut
    ? () => {
        void (async () => {
          if (state.view === 'editor' && !(await state.flushPendingSegmentSave())) return;
          onSignOut();
        })();
      }
    : undefined;

  let content: ReactNode;
  if (state.view === 'editor' && state.activeTask) {
    content = (
      <ReasonEditorBoundary
        onRuntimeError={() => {
          void state.recordClientEvent({
            event: 'editor_runtime_error',
            ...diagnosticContext(),
          });
        }}
        onRecover={() => {
          if (state.activeTask) void state.openTask(state.activeTask.episode_id);
        }}
      >
        <ReasonEditor
          task={state.activeTask}
          segmentIndex={state.activeSegmentIndex}
          saving={state.saving}
          pendingSaveCount={state.pendingSegmentSaveCount}
          segmentSaveStates={state.segmentSaveStates}
          committing={state.committing}
          commitStage={state.commitStage}
          options={state.reasonOptions}
          nowMs={nowMs}
          observedMediaDurationS={state.activeMediaDurationS}
          onBack={() => {
            sessionStorage.removeItem(REASON_EDITOR_SESSION_KEY);
            void state.closeTask();
          }}
          onSegmentChange={(index) => void state.navigateToSegment(index)}
          onReasonsChange={(segmentIndex, reasons) => void state.saveReasons(segmentIndex, reasons)}
          onReasonNoteChange={state.updateReasonNote}
          onMediaDuration={state.setActiveMediaDuration}
          onCommit={() => void state.commitActiveTask()}
        />
      </ReasonEditorBoundary>
    );
  } else if (state.view === 'admin' && isAdmin) {
    content = (
      <ReasonAdmin
        tasks={state.adminTasks}
        stats={state.reasonStats}
        loading={state.loadingAdmin}
        loadingStats={state.loadingStats}
        hasMore={state.adminHasMore}
        nowMs={nowMs}
        onRefresh={() => void Promise.all([state.loadAdminTasks(), state.loadStats()])}
        onSearch={(query) => void state.searchAdminTasks(query)}
        onLoadMore={() => void state.loadMoreAdminTasks()}
        submissions={state.adminSubmissions}
        onLoadSubmissions={(episodeId) => void state.loadSubmissionHistory(episodeId)}
        onReassign={state.reassignTask}
        onRetrySync={(episodeId) => void state.retrySync(episodeId)}
        onRefreshSource={(episodeId) => void state.refreshSource(episodeId)}
        onDiagnoseSource={state.diagnoseSource}
        ingestionRunning={state.ingestionRunning}
        ingestionResult={state.ingestionResult}
        onRunIngestion={(dryRun, maxPages, fromBeginning) =>
          void state.runIngestion(dryRun, maxPages, fromBeginning)
        }
        segmentMigrationRunning={state.segmentMigrationRunning}
        segmentMigrationResult={state.segmentMigrationResult}
        onMigrateSegmentContract={(dryRun, limit, options) =>
          void state.migrateSegmentContractV2(dryRun, limit, options)
        }
      />
    );
  } else if (state.view === 'result' && isAdmin && state.activeResult) {
    content = (
      <ReasonResultViewer
        result={state.activeResult}
        segmentIndex={state.activeResultSegmentIndex}
        loading={state.loadingResult}
        onBack={state.closeResult}
        onRevisionChange={(revision) =>
          void state.openResult(state.activeResult!.episode_id, revision)
        }
        onSegmentChange={state.navigateToResultSegment}
      />
    );
  } else if (state.view === 'results' && isAdmin) {
    content = (
      <ReasonResults
        results={state.results}
        filters={state.resultFilters}
        loading={state.loadingResults}
        loadingMore={state.loadingMoreResults}
        hasMore={state.resultNextCursor !== null}
        onSearch={(filters) => void state.searchResults(filters)}
        onLoadMore={() => void state.loadMoreResults()}
        onOpen={(episodeId) => void state.openResult(episodeId)}
      />
    );
  } else if (state.view === 'leaderboard' && isAdmin) {
    content = (
      <ReasonLeaderboard
        data={state.leaderboard}
        loading={state.loadingLeaderboard}
        updatedAtMs={state.leaderboardUpdatedAtMs}
        onRefresh={() => void state.loadLeaderboard()}
      />
    );
  } else {
    content = (
      <ReasonMyTasks
        tasks={state.tasks}
        summary={state.taskSummary}
        filter={state.taskFilter}
        query={state.taskQuery}
        sort={state.taskSort}
        order={state.taskOrder}
        loading={state.loadingTasks}
        loadingMore={state.loadingMoreTasks}
        hasMore={state.taskNextCursor !== null}
        claiming={state.claiming}
        nowMs={nowMs}
        onFilterChange={state.setTaskFilter}
        onSearch={(query) => void state.searchMyTasks(query)}
        onSortChange={(sort, order) => void state.sortMyTasks(sort, order)}
        onLoadMore={() => void state.loadMoreMyTasks()}
        onClaim={() => void state.claimNext()}
        onOpen={(episodeId) => void state.openTask(episodeId)}
      />
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-brand-dark text-brand-text-primary">
      <ReasonHeader
        view={state.view}
        operator={operator}
        isAdmin={Boolean(isAdmin)}
        {...(state.runtimeInfo
          ? {
              environment: state.runtimeInfo.environment,
              workerVersion: state.runtimeInfo.worker_version,
            }
          : {})}
        onNavigate={(view) => void state.setView(view)}
        {...(handleSignOut ? { onSignOut: handleSignOut } : {})}
      />
      <ErrorBanner />
      <NoticeBanner />
      {content}
    </div>
  );
}

export function SecCapReasonApp({
  operator,
  isAdmin = false,
  client = secCapReasonApi,
  now,
  onSignOut,
}: SecCapReasonAppProps) {
  useEffect(() => {
    const previousOverflowX = document.documentElement.style.overflowX;
    document.documentElement.style.overflowX = 'clip';
    return () => {
      document.documentElement.style.overflowX = previousOverflowX;
    };
  }, []);

  return (
    <SecCapReasonStoreProvider client={client} {...(now ? { now } : {})}>
      <SecCapReasonWorkspace
        operator={operator}
        isAdmin={isAdmin}
        {...(now ? { now } : {})}
        {...(onSignOut ? { onSignOut } : {})}
      />
    </SecCapReasonStoreProvider>
  );
}
